import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { handleDatabaseError } from '../utils/errorHandler';

type UserRole = 'super_admin' | 'admin' | 'manager' | 'staff' | 'student';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarUrl?: string;
  onboardingCompleted?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (data: Partial<AuthUser>) => Promise<void>;
  hasPermission: (requiredRole: UserRole) => boolean;
  isStudent: () => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  needsOnboarding: () => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ROLES_HIERARCHY: Record<UserRole, UserRole[]> = {
  super_admin: ['super_admin', 'admin', 'manager', 'staff', 'student'],
  admin: ['admin', 'manager', 'staff', 'student'],
  manager: ['manager', 'staff', 'student'],
  staff: ['staff', 'student'],
  student: ['student'],
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await updateUserData(session.user);
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
          if (session?.user) {
            await updateUserData(session.user);
          } else {
            setUser(null);
          }
          setLoading(false);
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth setup error:', error);
        setLoading(false);
      }
    };

    setupAuth();
  }, []);

  const updateUserData = async (supabaseUser: User) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role, full_name, avatar_url, onboarding_completed')
        .eq('id', supabaseUser.id)
        .single();

      if (error) throw error;

      setUser({
        id: supabaseUser.id,
        name: profile?.full_name || supabaseUser.email?.split('@')[0] || 'Unknown User',
        email: supabaseUser.email || '',
        role: profile?.role || 'student',
        avatarUrl: profile?.avatar_url,
        onboardingCompleted: profile?.onboarding_completed || false,
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUser({
        id: supabaseUser.id,
        name: supabaseUser.email?.split('@')[0] || 'Unknown User',
        email: supabaseUser.email || '',
        role: 'student',
        onboardingCompleted: false,
      });
    }
  };

  const handleAuthError = (error: any) => {
    console.error('Auth error:', error);
    
    const errorMessages: Record<string, string> = {
      'Invalid login credentials': 'Invalid email or password',
      'Email not confirmed': 'Please verify your email address',
      'User already registered': 'An account with this email already exists',
    };

    throw new Error(errorMessages[error.message] || error.message);
  };

  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error) {
      handleAuthError(error);
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role: 'student',
          },
        },
      });
      if (error) throw error;
    } catch (error) {
      handleAuthError(error);
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw new Error('Failed to log out');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (error) {
      handleAuthError(error);
    }
  };

  const updateProfile = async (data: Partial<AuthUser>) => {
    if (!user?.id) throw new Error('No user logged in');

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.name,
          avatar_url: data.avatarUrl,
          onboarding_completed: data.onboardingCompleted,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      setUser(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Failed to update profile');
    }
  };

  const hasPermission = (requiredRole: UserRole): boolean => {
    if (!user) return false;
    return ROLES_HIERARCHY[user.role].includes(requiredRole);
  };

  const isStudent = (): boolean => {
    return user?.role === 'student';
  };

  const isAdmin = (): boolean => {
    return user?.role === 'admin' || user?.role === 'super_admin';
  };

  const isSuperAdmin = (): boolean => {
    return user?.role === 'super_admin';
  };

  const needsOnboarding = (): boolean => {
    return isStudent() && !user?.onboardingCompleted;
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    resetPassword,
    updateProfile,
    hasPermission,
    isStudent,
    isAdmin,
    isSuperAdmin,
    needsOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;